import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import type { TranslationKey } from '../i18n/translations'
import { reportsApi } from '../lib/api'
import { btnAccentSm, btnNav, pageTitle, sectionTitle, segGroup, segItem } from '../lib/ui'

const CATEGORY_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6']

/** 明細セルを取引一覧へのリンクにするときの見た目。Balance のセルリンクと揃えている */
const drillLink = 'block rounded hover:underline underline-offset-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400'

const pad2 = (n: number) => String(n).padStart(2, '0')

const lastDayOf = (year: number, month: number) => new Date(year, month, 0).getDate()

interface MonthlySummary {
  year: number
  month: number
  income: number
  expense: number
  balance: number
  previousMonth: { income: number; expense: number; balance: number }
  expenseChangeRate: number
}

interface CategoryBreakdown {
  categoryId: string
  categoryName: string
  amount: number
  ratio: number
}

interface DailyTrendItem {
  day: number
  income: number
  expense: number
  balance: number
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  }
}

// 始点と終点が一致する円弧は SVG では描画されないため、
// ほぼ全周を占めるスライスは path ではなく circle で描く
const FULL_CIRCLE_THRESHOLD = 359.99

function slicePath(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle)
  const end = polarToCartesian(cx, cy, r, startAngle)
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1'
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`
}

interface TooltipPos {
  x: number
  y: number
}

function Tooltip({ pos, children }: { pos: TooltipPos; children: React.ReactNode }) {
  return (
    <div
      className="fixed z-50 pointer-events-none bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg min-w-[120px]"
      style={{ left: pos.x + 12, top: pos.y - 8 }}
    >
      {children}
    </div>
  )
}

function PieChart({ categories }: { categories: CategoryBreakdown[] }) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [tooltip, setTooltip] = useState<{ pos: TooltipPos; slice: CategoryBreakdown & { color: string } } | null>(null)

  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">{t('common_no_data')}</div>
    )
  }
  let cumulative = 0
  const slices = categories.map((cat, i) => {
    const startAngle = cumulative * 360
    cumulative += cat.ratio
    const endAngle = cumulative * 360
    return { ...cat, startAngle, endAngle, color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }
  })

  return (
    <div>
      <svg viewBox="0 0 200 200" className="w-full max-w-[200px] mx-auto">
        {slices.map((slice, i) => {
          const shapeProps = {
            fill: slice.color,
            stroke: 'white',
            strokeWidth: '1',
            className: 'cursor-pointer transition-opacity hover:opacity-75',
            onMouseMove: (e: React.MouseEvent) => setTooltip({ pos: { x: e.clientX, y: e.clientY }, slice }),
            onMouseLeave: () => setTooltip(null),
          }
          return slice.endAngle - slice.startAngle >= FULL_CIRCLE_THRESHOLD ? (
            <circle key={i} cx={100} cy={100} r={80} {...shapeProps} />
          ) : (
            <path key={i} d={slicePath(100, 100, 80, slice.startAngle, slice.endAngle)} {...shapeProps} />
          )
        })}
      </svg>
      {tooltip && (
        <Tooltip pos={tooltip.pos}>
          <p className="font-semibold mb-1">{tooltip.slice.categoryName}</p>
          <p>{formatJPY(tooltip.slice.amount)}</p>
          <p className="text-gray-300">{(tooltip.slice.ratio * 100).toFixed(1)}%</p>
        </Tooltip>
      )}
    </div>
  )
}

interface TrendBar {
  /** 横軸ラベル（月別なら「7月」、日別なら「15」） */
  label: string
  /** ツールチップ見出し */
  tooltipTitle: string
  income: number
  expense: number
  balance: number
}

function BarChart({ bars }: { bars: TrendBar[] }) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [tooltip, setTooltip] = useState<{ pos: TooltipPos; bar: TrendBar; type: 'income' | 'expense' } | null>(null)

  if (bars.length === 0) {
    return (
      <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">{t('common_no_data')}</div>
    )
  }
  const maxVal = Math.max(...bars.flatMap((b) => [b.income, b.expense]), 1)
  const chartH = 140
  // 横幅は固定し、本数（月別12本／日別最大31本）に応じて棒を詰める。
  // こうすると本数が増えてもグラフの縦横比とラベルの読める大きさが変わらない
  const viewW = 480
  const dense = bars.length > 16
  const slotW = (viewW - 20) / bars.length
  const gap = dense ? 1 : 4
  const barW = Math.max(3, Math.min(16, (slotW - gap - 4) / 2))
  const labelFontSize = dense ? 7 : 9

  return (
    <div>
      <svg viewBox={`0 0 ${viewW} 180`} className="w-full">
        {bars.map((b, i) => {
          const x = 10 + i * slotW
          const incomeH = (b.income / maxVal) * chartH
          const expenseH = (b.expense / maxVal) * chartH
          const labelX = x + barW + gap / 2
          return (
            <g key={i}>
              <rect
                x={x}
                y={chartH - incomeH + 20}
                width={barW}
                height={incomeH}
                fill="#22c55e"
                rx="2"
                className="cursor-pointer transition-opacity hover:opacity-75"
                onMouseMove={(e) => setTooltip({ pos: { x: e.clientX, y: e.clientY }, bar: b, type: 'income' })}
                onMouseLeave={() => setTooltip(null)}
              />
              <rect
                x={x + barW + gap}
                y={chartH - expenseH + 20}
                width={barW}
                height={expenseH}
                fill="#ef4444"
                rx="2"
                className="cursor-pointer transition-opacity hover:opacity-75"
                onMouseMove={(e) => setTooltip({ pos: { x: e.clientX, y: e.clientY }, bar: b, type: 'expense' })}
                onMouseLeave={() => setTooltip(null)}
              />
              <text
                x={labelX}
                y={168}
                textAnchor="middle"
                fontSize={labelFontSize}
                fill="#9ca3af"
              >
                {b.label}
              </text>
            </g>
          )
        })}
      </svg>
      {tooltip && (
        <Tooltip pos={tooltip.pos}>
          <p className="font-semibold mb-1">{tooltip.bar.tooltipTitle}</p>
          <p className={tooltip.type === 'income' ? 'text-green-400' : 'text-red-400'}>
            {t(tooltip.type === 'income' ? 'reports_tooltip_income_label' as TranslationKey : 'reports_tooltip_expense_label' as TranslationKey)
              .replace('{amount}', formatJPY(tooltip.type === 'income' ? tooltip.bar.income : tooltip.bar.expense))}
          </p>
          <p className="text-gray-300 mt-0.5">
            {t('reports_tooltip_balance_label').replace('{amount}', formatJPY(tooltip.bar.balance))}
          </p>
        </Tooltip>
      )}
    </div>
  )
}

type ViewMode = 'monthly' | 'annual'

interface AnnualSummary {
  year: number
  totalIncome: number
  totalExpense: number
  totalBalance: number
  months: Array<{ month: number; income: number; expense: number; balance: number }>
  topExpenseCategories: CategoryBreakdown[]
}

export default function Reports() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const [viewMode, setViewMode] = useState<ViewMode>('monthly')
  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState(currentMonth)

  const [summary, setSummary] = useState<MonthlySummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState<string | null>(null)

  const [categories, setCategories] = useState<CategoryBreakdown[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  const [daily, setDaily] = useState<DailyTrendItem[]>([])
  const [dailyLoading, setDailyLoading] = useState(true)

  const [annualData, setAnnualData] = useState<AnnualSummary | null>(null)
  const [annualLoading, setAnnualLoading] = useState(true)
  const [annualError, setAnnualError] = useState<string | null>(null)

  const prevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12) }
    else setMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1) }
    else setMonth((m) => m + 1)
  }
  const prevYear = () => setYear((y) => y - 1)
  const nextYear = () => setYear((y) => y + 1)
  const goToCurrent = () => { setYear(currentYear); setMonth(currentMonth) }

  // 年間表示で年を進めたあと月間に戻ると未来月になり得るので、今月まで戻す
  useEffect(() => {
    if (viewMode !== 'monthly') return
    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      setYear(currentYear)
      setMonth(currentMonth)
    }
  }, [viewMode, year, month, currentYear, currentMonth])

  useEffect(() => {
    if (!accessToken || viewMode !== 'monthly') return
    setSummaryLoading(true)
    setSummaryError(null)
    reportsApi.monthlySummary(accessToken, year, month)
      .then((res) => setSummary(res.data))
      .catch((err: unknown) => setSummaryError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setSummaryLoading(false))
  }, [accessToken, year, month, viewMode])

  useEffect(() => {
    if (!accessToken || viewMode !== 'monthly') return
    setCategoriesLoading(true)
    reportsApi.categoryBreakdown(accessToken, year, month, 'expense')
      .then((res) => setCategories(res.data.categories))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false))
  }, [accessToken, year, month, viewMode])

  useEffect(() => {
    if (!accessToken || viewMode !== 'monthly') return
    setDailyLoading(true)
    reportsApi.dailyCalendar(accessToken, year, month)
      .then((res) => {
        // API は取引のある日だけ返すため、月の全日を 0 埋めして並べる
        const byDay = new Map(res.data.days.map((d) => [d.day, d]))
        const lastDay = new Date(year, month, 0).getDate()
        setDaily(
          Array.from({ length: lastDay }, (_, i) => {
            const day = i + 1
            const entry = byDay.get(day)
            const income = entry?.income ?? 0
            const expense = entry?.expense ?? 0
            return { day, income, expense, balance: income - expense }
          }),
        )
      })
      .catch(() => setDaily([]))
      .finally(() => setDailyLoading(false))
  }, [accessToken, year, month, viewMode])

  useEffect(() => {
    if (!accessToken || viewMode !== 'annual') return
    setAnnualLoading(true)
    setAnnualError(null)
    reportsApi.annualSummary(accessToken, year)
      .then((res) => setAnnualData(res.data))
      .catch((err: unknown) => setAnnualError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setAnnualLoading(false))
  }, [accessToken, year, viewMode])

  const isAnnual = viewMode === 'annual'

  // 未来は集計対象が無いので進めない
  const canGoNext = isAnnual
    ? year < currentYear
    : year < currentYear || (year === currentYear && month < currentMonth)
  const isAtCurrent = isAnnual ? year === currentYear : year === currentYear && month === currentMonth

  const displaySummary = isAnnual
    ? (annualData ? { income: annualData.totalIncome, expense: annualData.totalExpense, balance: annualData.totalBalance } : null)
    : (summary ? { income: summary.income, expense: summary.expense, balance: summary.balance } : null)
  const displaySummaryLoading = isAnnual ? annualLoading : summaryLoading
  const displaySummaryError = isAnnual ? annualError : summaryError

  const displayCategories = isAnnual ? annualData?.topExpenseCategories ?? [] : categories
  const displayCategoriesLoading = isAnnual ? annualLoading : categoriesLoading

  // 月間表示は選択月の日別、年間表示は 12 か月分の推移を出す
  const displayBars: TrendBar[] = isAnnual
    ? (annualData?.months.map((m) => ({
        label: t('common_month_n').replace('{n}', String(m.month)),
        tooltipTitle: t('common_year_month').replace('{year}', String(annualData.year)).replace('{month}', String(m.month)),
        income: m.income,
        expense: m.expense,
        balance: m.balance,
      })) ?? [])
    : daily.map((d) => ({
        label: String(d.day),
        tooltipTitle: t('common_year_month_day')
          .replace('{year}', String(year))
          .replace('{month}', String(month))
          .replace('{day}', String(d.day)),
        income: d.income,
        expense: d.expense,
        balance: d.balance,
      }))
  const displayTrendLoading = isAnnual ? annualLoading : dailyLoading

  const trendTitle = isAnnual
    ? t('reports_trend_title_annual').replace('{year}', String(year))
    : t('reports_trend_title_daily').replace('{year}', String(year)).replace('{month}', String(month))

  // 明細の行から、そのカテゴリ・表示中の期間で絞り込んだ取引一覧へ飛べるようにする
  const categoryHref = useCallback(
    (categoryId: string) => {
      const params = new URLSearchParams({ type: 'expense', category: categoryId })
      params.set('dateFrom', isAnnual ? `${year}-01-01` : `${year}-${pad2(month)}-01`)
      params.set('dateTo', isAnnual ? `${year}-12-31` : `${year}-${pad2(month)}-${pad2(lastDayOf(year, month))}`)
      return `/transactions?${params.toString()}`
    },
    [isAnnual, year, month],
  )

  const drillTitle = t('reports_open_transactions')

  const balanceColorClass = displaySummary
    ? displaySummary.balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
    : 'text-gray-900 dark:text-gray-100'

  return (
    <div>
      <h1 className={pageTitle}>{t('reports_title')}</h1>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className={segGroup}>
          <button
            onClick={() => setViewMode('monthly')}
            className={segItem(viewMode === 'monthly')}
          >
            {t('reports_view_monthly')}
          </button>
          <button
            onClick={() => setViewMode('annual')}
            className={segItem(viewMode === 'annual')}
          >
            {t('reports_view_annual')}
          </button>
        </div>

        <button
          onClick={isAnnual ? prevYear : prevMonth}
          className={btnNav}
        >
          ◀
        </button>
        <span className="text-base font-semibold text-gray-900 min-w-[7rem] text-center dark:text-gray-100">
          {isAnnual
            ? t('reports_year_label').replace('{year}', String(year))
            : t('common_year_month').replace('{year}', String(year)).replace('{month}', String(month))}
        </span>
        <button
          onClick={isAnnual ? nextYear : nextMonth}
          disabled={!canGoNext}
          className={btnNav}
        >
          ▶
        </button>
        <button
          onClick={goToCurrent}
          disabled={isAtCurrent}
          className={btnAccentSm}
        >
          {t(isAnnual ? 'reports_jump_current_year' : 'reports_jump_current_month')}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports_income')}</p>
          <p className="mt-2 text-2xl font-semibold text-green-600 dark:text-green-400">
            {displaySummaryLoading ? '...' : formatJPY(displaySummary?.income ?? 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports_expense')}</p>
          <p className="mt-2 text-2xl font-semibold text-red-600 dark:text-red-400">
            {displaySummaryLoading ? '...' : formatJPY(displaySummary?.expense ?? 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports_balance')}</p>
          <p className={`mt-2 text-2xl font-semibold ${balanceColorClass}`}>
            {displaySummaryLoading ? '...' : formatJPY(displaySummary?.balance ?? 0)}
          </p>
        </div>
      </div>

      {displaySummaryError && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400">{displaySummaryError}</p>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className={`${sectionTitle} mb-4`}>{trendTitle}</h2>
          {displayTrendLoading ? (
            <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
          ) : (
            <BarChart bars={displayBars} />
          )}
          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-green-500" />{t('reports_income')}</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-500" />{t('reports_expense')}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          <h2 className={`${sectionTitle} mb-4`}>{t('reports_category_title')}</h2>
          {displayCategoriesLoading ? (
            <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
          ) : (
            <PieChart categories={displayCategories} />
          )}
        </div>
      </div>

      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden dark:bg-gray-900 dark:border-gray-800">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
          <h2 className={sectionTitle}>{t('reports_category_detail_title')}</h2>
        </div>
        {displayCategoriesLoading ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
        ) : displayCategories.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('reports_no_data')}</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[420px]">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('reports_col_category')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('reports_col_amount')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('reports_col_ratio')}</th>
              </tr>
            </thead>
            <tbody>
              {displayCategories.map((cat, i) => {
                const href = categoryHref(cat.categoryId)
                return (
                  <tr key={cat.categoryId} className="border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800">
                    <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100">
                      <Link to={href} title={drillTitle} className={`${drillLink} flex items-center gap-2`}>
                        <span
                          className="inline-block w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}
                        />
                        {cat.categoryName}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 text-sm text-gray-900 text-right dark:text-gray-100">
                      <Link to={href} title={drillTitle} className={drillLink}>{formatJPY(cat.amount)}</Link>
                    </td>
                    <td className="px-3 py-1.5 text-sm text-gray-500 text-right dark:text-gray-400">
                      <Link to={href} title={drillTitle} className={drillLink}>{(cat.ratio * 100).toFixed(1)}%</Link>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  )
}
